import { useMemo, useState } from 'react';
import { useField, observer } from '@formily/react';
import { Field } from '@formily/core';
import classNames from 'classnames';
import { BadTwo, GoodTwo } from '@icon-park/react';
import { imgCdnHostPrefix, imgCdnHostSuffix } from '../../common/const';
import { QuestionConfig, TrueOrFalseData } from '../../types/feelgood';
import ComponentStore from '../../store';
import styles from './style.module.less';
import '@smastrom/react-rating/style.css';

export enum TFStyleType {
  Emoij = 1,
  Icon = 2,
  Number = 3,
}

const TrueFalseIcon = ({
  tfData,
  field,
  setHoveredText,
}: {
  tfData: TrueOrFalseData;
  field: Field;
  setHoveredText: (text: string) => void;
}) => {
  const [currentValue, setCurrentValue] = useState('');
  const isGrayTrue = useMemo(() => {
    let temp = false;
    if (field.value) {
      temp = tfData.trueOptionKey !== field.value?.optionKey;
    }
    if (currentValue && tfData.trueOptionKey !== field.value?.optionKey) {
      temp = tfData.trueOptionKey !== currentValue;
    }
    return temp;
  }, [currentValue, field.value, tfData.trueOptionKey]);

  const isGrayFalse = useMemo(() => {
    let temp = false;
    if (field.value) {
      temp = tfData.falseOptionKey !== field.value?.optionKey;
    }
    if (currentValue && tfData.falseOptionKey !== field.value?.optionKey) {
      temp = tfData.falseOptionKey !== currentValue;
    }
    return temp;
  }, [currentValue, field.value, tfData.falseOptionKey]);

  const setValue = (val: boolean) => {
    field.setValue({
      optionKey: val ? tfData.trueOptionKey : tfData.falseOptionKey,
      answer: val,
    });
    field.validate();
  };

  return (
    <div className={classNames(styles.emojiList)}>
      {tfData.style === TFStyleType.Emoij && (
        <>
          <img
            className={classNames(
              styles.emojiIcon,
              isGrayFalse && styles.emojiIconGray,
            )}
            src={`${imgCdnHostPrefix}rating-emoji-1.${
              tfData.falseOptionKey === currentValue ? 'gif' : 'png'
            }${imgCdnHostSuffix}`}
            onMouseOver={() => {
              setHoveredText(tfData.falseTitleText);
              setCurrentValue(tfData.falseOptionKey);
            }}
            onMouseLeave={() => {
              setHoveredText('');
              setCurrentValue('');
            }}
            onClick={() => {
              setValue(false);
            }}
          />
          <img
            className={classNames(
              styles.emojiIcon,
              isGrayTrue && styles.emojiIconGray,
            )}
            src={`${imgCdnHostPrefix}rating-emoji-5.${
              tfData.trueOptionKey === currentValue ? 'gif' : 'png'
            }${imgCdnHostSuffix}`}
            onMouseOver={() => {
              setHoveredText(tfData.trueTitleText);
              setCurrentValue(tfData.trueOptionKey);
            }}
            onMouseLeave={() => {
              setHoveredText('');
              setCurrentValue('');
            }}
            onClick={() => {
              setValue(true);
            }}
          />
        </>
      )}
      {tfData.style === TFStyleType.Icon && (
        <>
          <BadTwo
            className={classNames(
              styles.okIcon,
              tfData.falseOptionKey === field.value?.optionKey &&
                styles.okIconClick,
            )}
            theme="filled"
            size="32"
            strokeLinecap="square"
            onMouseOver={() => {
              setHoveredText(tfData.falseTitleText);
              setCurrentValue(tfData.falseOptionKey);
            }}
            onMouseLeave={() => {
              setHoveredText('');
              setCurrentValue('');
            }}
            onClick={() => {
              setValue(false);
            }}
          />
          <GoodTwo
            className={classNames(
              styles.okIcon,
              tfData.trueOptionKey === field.value?.optionKey &&
                styles.okIconClick,
            )}
            theme="filled"
            size="32"
            strokeLinecap="square"
            onMouseOver={() => {
              setHoveredText(tfData.trueTitleText);
              setCurrentValue(tfData.trueOptionKey);
            }}
            onMouseLeave={() => {
              setHoveredText('');
              setCurrentValue('');
            }}
            onClick={() => {
              setValue(true);
            }}
          />
        </>
      )}
      {tfData.style === TFStyleType.Number && (
        <>
          <div
            className={classNames(
              styles.number,
              tfData.falseOptionKey === field.value?.optionKey &&
                styles.numberClick,
            )}
            onClick={() => {
              setValue(false);
            }}
          >
            {tfData.falseTitleText}
          </div>
          <div
            className={classNames(
              styles.number,
              tfData.trueOptionKey === field.value?.optionKey &&
                styles.numberClick,
            )}
            onClick={() => {
              setValue(true);
            }}
          >
            {tfData.trueTitleText}
          </div>
        </>
      )}
    </div>
  );
};

/**
 * 评分
 */
export default observer(() => {
  const [hoveredText, setHoveredText] = useState('');
  const { defaultText, defaultTextSign } = ComponentStore.useContext();
  const field = useField<Field>();
  const questionConfig: QuestionConfig = field.data;
  const tfData = useMemo(() => {
    if (questionConfig.tfData) {
      return {
        ...questionConfig.tfData,
        trueTitleText:
          questionConfig?.tfData?.trueTitleText === defaultTextSign
            ? defaultText?.[`true-false-true`]
            : questionConfig?.tfData?.trueTitleText,
        falseTitleText:
          questionConfig?.tfData?.falseTitleText === defaultTextSign
            ? defaultText?.[`true-false-false`]
            : questionConfig?.tfData?.falseTitleText,
      };
    }
    return undefined;
  }, [defaultText, defaultTextSign, questionConfig.tfData]);

  const text = useMemo(() => {
    if (field.value) {
      return tfData?.trueOptionKey === field.value?.optionKey
        ? tfData?.trueTitleText
        : tfData?.falseTitleText;
    } else {
      return '';
    }
  }, [field.value, tfData]);

  const feedbackText = useMemo(() => hoveredText || text, [hoveredText, text]);

  field.setValidator(val => {
    if (questionConfig.tfData?.isRequired && !val) {
      return defaultText?.is_require;
    }
    return true;
  });
  return tfData ? (
    <div className={classNames(styles.tf, 'feelgoodTrueFalse')}>
      <TrueFalseIcon
        tfData={tfData}
        field={field}
        setHoveredText={setHoveredText}
      />
      {questionConfig.tfData?.style !== TFStyleType.Number && (
        <div className={styles.tf__feedback}>{feedbackText}</div>
      )}
    </div>
  ) : null;
});
